import { Button, Popover, PopoverAnchor, PopoverContent, Tooltip } from '@ibsys/kit';
import { XIcon } from 'lucide-react';
import { FC, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationSelectionSource } from '@/entities/conversations';
import { useTablePresets } from '@/entities/settings';
import { formatHumanDate } from '@/shared/lib';

type Props = {
  /** Имя таблицы — соответствует ключу в `settings.tables` (например, `'dialogsListV2'`) */
  tableName: string;
  /** Несохранённая выборка (описание + источник); `null` — окно не показывается */
  selection: { description: string; source: ConversationSelectionSource } | null;
  /** Пользователь скрыл окно, не сохраняя выборку */
  onDismiss: () => void;
  /** Элемент-якорь, рядом с которым появляется окно (кнопка пресетов) */
  children: ReactNode;
};

/** i18n-ключи автоимени пресета по источнику выборки. */
const PRESET_NAME_KEY = {
  autoInsight: 'tablePresets:autoInsightPresetName',
  qualityZone: 'tablePresets:qualityZonePresetName',
  consultantRating: 'tablePresets:consultantRatingPresetName',
  criteriaAverage: 'tablePresets:criteriaAveragePresetName',
  lowScore: 'tablePresets:lowScorePresetName',
  distribution: 'tablePresets:distributionPresetName',
  trend: 'tablePresets:trendPresetName',
  alert: 'tablePresets:alertPresetName',
  customChart: 'tablePresets:customChartPresetName',
} as const satisfies Record<ConversationSelectionSource, string>;

/**
 * Окно сохранения внешней выборки в пресет таблицы.
 *
 * Показывается, пока список открыт по выборке, посчитанной снаружи, она не сохранена и окно
 * не скрыто пользователем: это единственная подсказка, что список сужен, а не полон. Название
 * пресета собирается автоматически из описания выборки — спрашивать его у пользователя незачем.
 * Шаблон имени зависит от источника выборки: «Авто-инсайт N. …», «Зона качества N. …»,
 * «Критерий N. …» и так далее по показателю дашборда, с которого пришла выборка.
 *
 * Ни сохранение, ни закрытие окна выборку не снимают: список остаётся суженным.
 */
export const SelectionSavePopover: FC<Props> = ({ tableName, selection, onDismiss, children }) => {
  const { t, i18n } = useTranslation(['tablePresets']);
  const { presets, savePresetAs } = useTablePresets(tableName);

  const handleSave = useCallback(() => {
    if (!selection) return;
    // Номер разводит одноимённые выборки одного источника: он на единицу больше, чем уже
    // сохранено пресетов того же источника у этого пользователя. Считаем по источнику, а не
    // по префиксу имени: имя локализовано, и при смене языка нумерация начиналась бы заново.
    const number = presets.filter((preset) => preset.selectionSource === selection.source).length + 1;
    // Дата попадает в имя всех показателей, кроме авто-инсайта: его шаблон параметр `date`
    // игнорирует, поэтому передаём дату безусловно.
    const date = formatHumanDate(i18n, new Date());
    const name = t(PRESET_NAME_KEY[selection.source], {
      number,
      description: selection.description,
      date,
    });
    savePresetAs(name, selection.source);
  }, [selection, presets, savePresetAs, t, i18n]);

  if (!selection) return <>{children}</>;

  return (
    <Popover open modal={false}>
      {/* Якорь оборачивает кнопку — без min-w-0 обёртка не даёт ей сжаться по доступному месту. */}
      <PopoverAnchor className="min-w-0">{children}</PopoverAnchor>
      <PopoverContent
        align="end"
        side="bottom"
        className="w-72 p-3 flex flex-col gap-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-start gap-2">
          <p className="flex-1 text-sm text-muted-foreground">{t('tablePresets:selectionSaveHint')}</p>
          <Tooltip tooltip={t('tablePresets:selectionDismiss')}>
            <Button
              variant="ghost"
              size="icon"
              className="size-6 shrink-0"
              aria-label={t('tablePresets:selectionDismiss')}
              icon={<XIcon className="size-3.5" />}
              onClick={onDismiss}
            />
          </Tooltip>
        </div>
        <Button size="sm" onClick={handleSave} className="self-end">
          {t('tablePresets:selectionSave')}
        </Button>
      </PopoverContent>
    </Popover>
  );
};
